import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DropdownList } from 'react-widgets';
import FormGroup from '../../../FormGroup';
import CustomModal from '../../../Modal';
import { I18nContext } from 'utils/I18n/context';

import PaymentSystem from '../PaymentSystems/PaymentSystem';

const PaymentPageSettingModal = ({
    visible,
    onClose,
    isCreate,
    payment,
    savePaymentAccount,
    onSubmit
}) => {
    const { t } = useContext(I18nContext);
    const [state, setState] = useState({
        currentPaymentId: null,
        currentPaymentSystemId: null,
        currentPaymentSystem: null,
        currencies: [],
        fullName: '',
        edrpouIpn: '',
        bankName: '',
        bankAccount: '',
        bic: '',
        purposeOfPayment: '',
        iban: '',
        cryptoCurrency: '',
        cryptoNetwork: '',
        cardNumber: '',
        description: '',
        currentCurrency: 'EUR',
        title: '',
        errors: {},
        loading: false,
        tags: null
    });

    const getCurrentPaymentInfo = useCallback((paymentId) => {
        let paymentMethodDetails = payment.paymentDetails.find(function(paymentDetails) {
            return paymentDetails.id === paymentId
        });

        let paymentSystem = payment.allowPaymentSystems.find(function(allowPaymentSystems) {
            return allowPaymentSystems.id === paymentMethodDetails.payment_dictionary.id
        });

        paymentMethodDetails.currentPaymentId = paymentMethodDetails.id;
        paymentMethodDetails.currentPaymentSystemId = paymentSystem.id;
        paymentMethodDetails.currentPaymentSystem = paymentSystem;
        paymentMethodDetails.currencies = paymentSystem.currency;
        paymentMethodDetails.currentCurrency = paymentMethodDetails.currency;

        return paymentMethodDetails;
    }, [payment]);

    const getPaymentSystemById = useCallback((paymentSystemId) => {
        let paymentSystems = payment.allowPaymentSystems;
        var returnPaymentSystem = paymentSystems[0];

        paymentSystems.forEach(function(paymentSystem) {
            if (parseInt(paymentSystem.id) === parseInt(paymentSystemId)) {
                returnPaymentSystem = paymentSystem;
            }
        });

        return returnPaymentSystem;
    }, [payment]);

    useEffect(() => {
        let currentPaymentId = payment && payment.currentPaymentId;
        let newState = {
            errors: {}
        };

        if (currentPaymentId) {
            let currentPaymentInfo = getCurrentPaymentInfo(currentPaymentId);

            newState.currentPaymentId = currentPaymentId;
            newState.currentPaymentSystemId = currentPaymentInfo.currentPaymentSystemId;
            newState.currentPaymentSystem = currentPaymentInfo.currentPaymentSystem;
            newState.currencies = currentPaymentInfo.currencies;
            newState.fullName = currentPaymentInfo.full_name;
            newState.edrpouIpn = currentPaymentInfo.edrpou_ipn;
            newState.bankName = currentPaymentInfo.bank_name;
            newState.bankAccount = currentPaymentInfo.bank_account;
            newState.bic = currentPaymentInfo.bic;
            newState.purposeOfPayment = currentPaymentInfo.purpose_of_payment;
            newState.iban = currentPaymentInfo.iban;
            newState.cryptoCurrency = currentPaymentInfo.crypto_currency;
            newState.cryptoNetwork = currentPaymentInfo.crypto_network;
            newState.cardNumber = currentPaymentInfo.card_number;
            newState.description = currentPaymentInfo.description;
            newState.currentCurrency = currentPaymentInfo.currency;
            newState.active = currentPaymentInfo.active;
            newState.title = currentPaymentInfo.title;
        } else {
            if (
                (payment.allowPaymentSystems.length
                || !state.currentPaymentSystemId) && getPaymentSystemById(null)
            ) {
                let currentPaymentSystem = getPaymentSystemById(null);
                let currencies = currentPaymentSystem.currency;

                newState.currentPaymentSystemId = currentPaymentSystem.id;
                newState.currentPaymentSystem = currentPaymentSystem;
                newState.currencies = currencies;
                newState.currentCurrency = currencies[0];
            } else {
                newState.currentPaymentSystemId = '';
                newState.currentPaymentSystem = '';
                newState.currencies = ['EUR'];
                newState.currentCurrency = 'EUR';
            }

            newState.currentPaymentId = null;
            newState.fullName = '';
            newState.edrpouIpn = '';
            newState.bankName = '';
            newState.bankAccount = '';
            newState.bic = '';
            newState.purposeOfPayment = '';
            newState.iban = '';
            newState.cryptoCurrency = '';
            newState.cryptoNetwork = '';
            newState.cardNumber = '';
            newState.description = '';
            newState.active = false;
            newState.title = '';
        }

        setState(prev => ({ ...prev, ...newState }));
    }, [payment, getCurrentPaymentInfo, getPaymentSystemById, state.currentPaymentSystemId]);

    const handleSaving = useCallback((e) => {
        e.preventDefault();

        setState(prev => ({
            ...prev,
            loading: true
        }));

        let params = {};

        params.currency = state.currentCurrency;
        params.currentPaymentId = state.currentPaymentId;
        params.paymentSystemId = state.currentPaymentSystemId;
        params.paymentSystem = state.currentPaymentSystem;
        params.fullName = state.fullName;
        params.edrpouIpn = state.edrpouIpn;
        params.bankName = state.bankName;
        params.bankAccount = state.bankAccount;
        params.bic = state.bic;
        params.purposeOfPayment = state.purposeOfPayment;
        params.iban = state.iban;
        params.cryptoCurrency = state.cryptoCurrency;
        params.cryptoNetwork = state.cryptoNetwork;
        params.cardNumber = state.cardNumber;
        params.description = state.description;
        params.title = state.title;

        savePaymentAccount(params, onSuccessSave, onErrorSave);
    }, [state, savePaymentAccount]);

    const onSuccessSave = useCallback(() => {
        setState(prev => ({
            ...prev,
            loading: false
        }));
        onSubmit();
    }, [onSubmit]);

    const onErrorSave = useCallback((errors) => {
        setState(prev => ({
            ...prev,
            loading: false,
            errors
        }));
    }, []);

    const setPaymentSystem = useCallback((event) => {
        if (!isCreate) {
            return;
        }

        const currentPaymentSystemId = event.target.closest('.btn-primary').getAttribute('data-id');
        const currentPaymentSystem = getPaymentSystemById(currentPaymentSystemId);
        const currencies = currentPaymentSystem.currency;

        setState(prev => ({
            ...prev,
            currentPaymentSystemId: currentPaymentSystemId,
            currentPaymentSystem: currentPaymentSystem,
            currencies: currencies,
            currentCurrency: currencies[0],
            fullName: '',
            edrpouIpn: '',
            bankName: '',
            bankAccount: '',
            bic: '',
            purposeOfPayment: '',
            iban: '',
            cryptoCurrency: '',
            cryptoNetwork: '',
            cardNumber: '',
            description: '',
            errors: {}
        }));
    }, [isCreate, getPaymentSystemById]);

    const setTitle = useCallback((e) => {
        setState(prev => ({
            ...prev,
            title: e.target.value
        }));
    }, []);

    const setCurrency = useCallback((item) => {
        const next = (item && item.title) ? item.title : item;
        setState(prev => ({
            ...prev,
            currentCurrency: next
        }));
    }, []);

    const setFullName = useCallback((e, value) => {
        if (value === undefined) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            fullName: value
        }));
    }, []);

    const setEdrpouIpn = useCallback((e, value) => {
        if (value === undefined) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            edrpouIpn: value
        }));
    }, []);

    const setBankName = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            bankName: value
        }));
    }, []);

    const setBankAccount = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            bankAccount: value
        }));
    }, []);

    const setBic = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            bic: value
        }));
    }, []);

    const setPurposeOfPayment = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            purposeOfPayment: value
        }));
    }, []);

    const setIban = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            iban: value
        }));
    }, []);

    const setCryptoCurrency = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            cryptoCurrency: value
        }));
    }, []);

    const setCryptoNetwork = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            cryptoNetwork: value
        }));
    }, []);

    const setCardNumber = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            cardNumber: value
        }));
    }, []);

    const setDescription = useCallback((e, value) => {
        if (undefined === value) {
            value = e.target.value;
        }

        setState(prev => ({
            ...prev,
            description: value
        }));
    }, []);

    const getPaymentSystemClasses = useCallback((paymentSystemId) => {
        return classNames({
            'btn': true,
            'dim': true,
            'btn-primary': true,
            'item': true,
            'active': parseInt(paymentSystemId) === parseInt(state.currentPaymentSystemId)
        });
    }, [state.currentPaymentSystemId]);

    const staticNames = t?.paymentPage?.settingModal || {
        accountName: 'Account Name',
        accountType: 'Account Type',
        accountCurrency: 'Account Currency',
        addAccount: 'Add Account',
        editAccount: 'Edit Account'
    };
    const staticNamesActions = t?.actions || { save: 'Save' };

    return (
        <CustomModal
            visible={visible}
            onClose={onClose}
            confirmName={staticNamesActions.save}
            loading={state.loading}
            isForm={true}
            title={isCreate ? staticNames.addAccount : staticNames.editAccount}
            onSubmit={handleSaving}
        >
            <div className="form-horizontal payment-method-form">
                <FormGroup
                    label={`${staticNames.accountName}`}
                    labelClass="required"
                    errors={state.errors && state.errors.title}
                >
                    <input
                        type="text"
                        className="form-control"
                        value={state.title}
                        onChange={setTitle}
                        required
                    />
                </FormGroup>
                <FormGroup label={`${staticNames.accountType}:`}>
                    <div className="payment-method__methods-list">
                        {payment.allowPaymentSystems.map(item => (
                            <div
                                className="list-item"
                                key={item.id}
                                onClick={setPaymentSystem}
                                data-id={item.id}
                            >
                                <input type="radio"
                                       className={getPaymentSystemClasses(item.id)}
                                       onClick={setPaymentSystem}
                                       data-id={item.id}
                                       checked={parseInt(item.id) === parseInt(state.currentPaymentSystemId)}
                                />
                                <span>{item.name}</span>
                            </div>
                        ))}
                    </div>
                </FormGroup>
                <FormGroup label={`${staticNames.accountCurrency}:`}>
                <DropdownList
                        className={'rw-dropdownlist-select'}
                        disabled={!isCreate}
                    data={Array.isArray(state.currencies) ? state.currencies.map(c => (typeof c === 'string' ? { title: c } : c)) : []}
                    dataKey={'title'}
                    textField={'title'}
                    value={(Array.isArray(state.currencies) ? state.currencies.map(c => (typeof c === 'string' ? { title: c } : c)) : []).find(c => c.title === state.currentCurrency) || null}
                        onChange={setCurrency}
                    />
                </FormGroup>
                <PaymentSystem
                    onChangeFullName={setFullName}
                    onChangeEdrpouIpn={setEdrpouIpn}
                    onChangeBankAccount={setBankAccount}
                    onChangeBankName={setBankName}
                    onChangeBic={setBic}
                    onChangePurposeOfPayment={setPurposeOfPayment}
                    onChangeIban={setIban}
                    onChangeCryptoCurrency={setCryptoCurrency}
                    onChangeCryptoNetwork={setCryptoNetwork}
                    onChangeCardNumber={setCardNumber}
                    onChangeDescription={setDescription}
                    fullName={state.fullName}
                    edrpouIpn={state.edrpouIpn}
                    bankName={state.bankName}
                    bankAccount={state.bankAccount}
                    bic={state.bic}
                    purposeOfPayment={state.purposeOfPayment}
                    iban={state.iban}
                    cryptoCurrency={state.cryptoCurrency}
                    cryptoNetwork={state.cryptoNetwork}
                    cardNumber={state.cardNumber}
                    description={state.description}
                    currentPaymentId={state.currentPaymentId}
                    id={state.currentPaymentSystemId}
                    paymentSystem={getPaymentSystemById(state.currentPaymentSystemId)}
                    countries={payment.countries}
                    errors={state.errors}
                />
            </div>
        </CustomModal>
    );
};

PaymentPageSettingModal.propTypes = {
    visible: PropTypes.bool,
    onClose: PropTypes.func,
    isCreate: PropTypes.bool,
    payment: PropTypes.object,
    savePaymentAccount: PropTypes.func,
    onSubmit: PropTypes.func
};

PaymentPageSettingModal.defaultProps = {
    visible: false,
    onClose: () => {},
    isCreate: false,
    payment: {},
    savePaymentAccount: () => {},
    onSubmit: () => {}
};

/**
 * @param {Boolean} isImage
 * @param {String} alt
 * @param {String} src
 * @param {String} className
 * @returns {JSX.Element}
 * @constructor
 */
export { PaymentPageSettingModal };